import React, { useEffect, useState } from 'react';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import DialogTitle from '@mui/joy/DialogTitle';
import DialogContent from '@mui/joy/DialogContent';
import Stack from '@mui/joy/Stack';
import Input from '@mui/joy/Input';
import Textarea from '@mui/joy/Textarea';
import Button from '@mui/joy/Button';
import Typography from '@mui/joy/Typography';
import { PaperPlaneTilt as SendIcon } from '@phosphor-icons/react';
import api from '../../lib/api';
import { EmailTemplatePicker } from './EmailTemplatePicker';

interface EmailComposeModalProps {
  open: boolean;
  /** Recipient resolved from the CRM contact — editable, never re-typed. */
  to: string;
  contactId?: number;
  contactName?: string;
  defaultSubject?: string;
  onClose: () => void;
  onSent?: () => void;
}

const parseAddresses = (value: string): string[] =>
  value.split(/[,;]/).map(a => a.trim()).filter(Boolean);

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography level="body-xs" sx={{ minWidth: 56, color: 'neutral.500', fontWeight: 'lg' }}>
    {children}
  </Typography>
);

/**
 * Compose a new email to an inbox row's contact, sent through the user's own
 * connected mailbox (POST /api/inbox/email/compose/).
 *
 * The EMAIL action used to hand off to `mailto:`, which dropped the thread,
 * the contact link and the timeline record — the same reason Call and Text
 * were moved off `tel:`/`sms:`.
 */
export const EmailComposeModal: React.FC<EmailComposeModalProps> = ({
  open,
  to,
  contactId,
  contactName,
  defaultSubject = '',
  onClose,
  onSent,
}) => {
  const [toAddresses, setToAddresses] = useState(to);
  const [subject, setSubject] = useState(defaultSubject);
  const [bodyText, setBodyText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed when the modal is opened for a different row. Only on open, so a
  // re-render never overwrites what the user has typed.
  useEffect(() => {
    if (!open) return;
    setToAddresses(to);
    setSubject(defaultSubject);
    setBodyText('');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const canSend = bodyText.trim() !== '' && subject.trim() !== '' && parseAddresses(toAddresses).length > 0;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      await api.post('/api/inbox/email/compose/', {
        to_addresses: parseAddresses(toAddresses),
        subject: subject.trim(),
        body_text: bodyText,
        body_html: `<p>${bodyText.replace(/\n/g, '<br>')}</p>`,
        ...(contactId ? { contact_id: contactId } : {}),
      });
      onSent?.();
      onClose();
    } catch (err: any) {
      setError(err?.responseData?.error || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="lg" sx={{ maxWidth: 600, width: '90vw' }}>
        <ModalClose />
        <DialogTitle>{contactName ? `Email ${contactName}` : 'New Email'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ pt: 1 }}>
            <EmailTemplatePicker
              contact={{ full_name: contactName, email: to }}
              onApply={(templateSubject, templateBody) => {
                // Fill the draft without clobbering it: anything already typed
                // stays, and the template lands underneath.
                setSubject(prev => prev.trim() || templateSubject);
                setBodyText(prev => (prev.trim() ? `${prev.trimEnd()}\n\n${templateBody}` : templateBody));
              }}
            />
            <Input
              size="sm"
              startDecorator={<FieldLabel>To</FieldLabel>}
              value={toAddresses}
              onChange={e => setToAddresses(e.target.value)}
            />
            <Input
              size="sm"
              startDecorator={<FieldLabel>Subject</FieldLabel>}
              value={subject}
              onChange={e => setSubject(e.target.value)}
            />
            <Textarea
              placeholder="Write your email…"
              value={bodyText}
              onChange={e => setBodyText(e.target.value)}
              minRows={6}
              maxRows={14}
              autoFocus
            />
            {error && (
              <Typography level="body-xs" sx={{ color: 'danger.500' }}>
                {error}
              </Typography>
            )}
            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
              <Typography level="body-xs" color="neutral">
                Sends from your connected email account
              </Typography>
              <Button
                size="sm"
                variant="solid"
                startDecorator={<SendIcon size={16} />}
                onClick={handleSend}
                disabled={!canSend || sending}
                loading={sending}
              >
                Send Email
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};

export default EmailComposeModal;
